import React, { useState, useEffect } from 'react';
import api from '../../api/api';
import ClientLayout from '../../components/ClientLayout';

// --- COLORES ---
const COLORS = {
    green: '#2E5B2E',
    pink: '#C19A6B',
    cream: '#F9F9F5',
    dark: '#333333',
    white: '#FFFFFF',
    red: '#DC2626',
    blue: '#2563EB',
    gray: '#9CA3AF'
};

function MisEmpleados() {
    const [empleados, setEmpleados] = useState([]);
    const [sucursales, setSucursales] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    
    // Estado para controlar si estamos editando
    const [editingId, setEditingId] = useState(null);

    // Estado del Formulario
    const [form, setForm] = useState({
        username: '',
        password: '',
        sucursalId: '' 
    });

    // Cargar datos iniciales
    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        const token = localStorage.getItem('token');
        if (!token) return;

        try {
            const [sucRes, empRes] = await Promise.all([
                api.get('/sucursales/me', { headers: { Authorization: `Bearer ${token}` } }),
                api.get('/users/empleados', { headers: { Authorization: `Bearer ${token}` } })
            ]);

            setSucursales(sucRes.data || []);
            setEmpleados(empRes.data || []);
            setLoading(false);
        } catch (error) {
            console.error("Error cargando datos", error);
            setLoading(false);
        }
    };

    // --- FUNCIÓN: PREPARAR EDICIÓN ---
    const handleEdit = (empleado) => {
        setEditingId(empleado.id);
        setForm({
            username: empleado.username,
            password: '', // Dejar vacía por seguridad (si no la quiere cambiar)
            sucursalId: empleado.sucursalId || empleado.sucursalAsignada?.id || ''
        });
    };

    // --- FUNCIÓN: CANCELAR EDICIÓN ---
    const handleCancelEdit = () => {
        setEditingId(null);
        setForm({ username: '', password: '', sucursalId: '' });
    };

    // --- FUNCIÓN: ELIMINAR ---
    const handleDelete = async (id, username) => {
        if (!window.confirm(`¿Estás seguro de eliminar al usuario "${username}"? Esta acción no se puede deshacer.`)) return;

        const token = localStorage.getItem('token');
        try {
            await api.delete(`/users/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            alert('🗑️ Usuario eliminado correctamente');
            fetchData(); // Recargar lista
        } catch (error) {
            console.error(error);
            alert('Error al eliminar el usuario.');
        }
    };

    // --- FUNCIÓN: GUARDAR (CREAR O EDITAR) ---
    const handleSubmit = async (e) => {
        e.preventDefault();
        const token = localStorage.getItem('token');
        setSaving(true);

        try {
            // Construir Payload
            const payload = {};
            if (form.username) payload.username = form.username;
            // Solo enviar contraseña si se escribió algo (para editar)
            if (form.password) payload.password = form.password;
            // Sucursal
            if (form.sucursalId) payload.sucursalId = form.sucursalId;

            if (editingId) {
                // --- MODO EDICIÓN (PATCH) ---
                if (!form.password) delete payload.password; // No actualizar pass si está vacío
                
                await api.patch(`/users/${editingId}`, payload, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                alert('✅ Datos actualizados correctamente');
                handleCancelEdit(); // Salir de modo edición

            } else {
                // --- MODO CREACIÓN (POST) ---
                // Validaciones extra solo para creación
                if (form.username.length < 4) throw new Error("El usuario debe tener al menos 4 letras.");
                if (form.password.length < 6) throw new Error("La contraseña debe tener al menos 6 caracteres.");

                await api.post('/users/empleados', payload, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                alert('✅ Usuario creado correctamente');
                setForm({ username: '', password: '', sucursalId: '' });
            }
            
            fetchData(); // Recargar lista

        } catch (error) {
            console.error(error);
            const msg = error.response?.data?.message || error.message;
            alert(`Error: ${Array.isArray(msg) ? msg.join(', ') : msg}`);
        } finally {
            setSaving(false);
        }
    };

    // Estilos
    const cardStyle = { background: COLORS.white, padding: 15, borderRadius: 12, boxShadow: '0 4px 10px rgba(0,0,0,0.05)', marginBottom: 15, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 };
    const labelStyle = { display: 'block', marginBottom: 5, fontWeight: 'bold', color: COLORS.dark, fontSize: 13 };
    const inputStyle = { width: '100%', padding: 12, borderRadius: 8, border: `1px solid #ccc`, fontSize: 14, marginBottom: 15 };
    const actionBtnStyle = { border: 'none', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 'bold', marginLeft: 5 };

    if (loading) return <div style={{textAlign:'center', marginTop:50}}>Cargando...</div>;

    return (
        <ClientLayout title="Gestión de Equipo">
            <div style={{ maxWidth: 1000, margin: '0 auto', display: 'flex', gap: 30, flexWrap: 'wrap-reverse' }}>
                
                {/* LISTA DE EMPLEADOS */}
                <div style={{ flex: '1 1 400px' }}>
                    <h3 style={{ color: COLORS.green, borderBottom: `1px solid ${COLORS.cream}`, paddingBottom: 10 }}>
                        👥 Cuentas Activas
                    </h3>
                    
                    {empleados.length === 0 ? (
                        <p style={{ fontStyle: 'italic', color: '#777' }}>No tienes usuarios registrados.</p>
                    ) : (
                        empleados.map(emp => (
                            <div key={emp.id} style={cardStyle}>
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontWeight: 'bold', fontSize: 16, color: COLORS.green }}>
                                        👤 {emp.username}
                                    </div>
                                    <div style={{ fontSize: 12, color: COLORS.pink, marginTop: 4 }}>
                                        {emp.sucursal || emp.sucursalAsignada 
                                            ? `📍 ${(emp.sucursal || emp.sucursalAsignada).nombre}` 
                                            : 'Sin sucursal asignada'}
                                    </div>
                                </div>
                                
                                {/* BOTONES DE ACCIÓN */}
                                <div style={{ display: 'flex', gap: 5 }}>
                                    <button 
                                        onClick={() => handleEdit(emp)}
                                        style={{ ...actionBtnStyle, background: COLORS.cream, color: COLORS.blue, border: `1px solid ${COLORS.blue}30` }}
                                        disabled={editingId === emp.id}
                                    >
                                        ✏️ Editar
                                    </button>
                                    <button 
                                        onClick={() => handleDelete(emp.id, emp.username)}
                                        style={{ ...actionBtnStyle, background: '#FEE2E2', color: COLORS.red, border: `1px solid ${COLORS.red}30` }}
                                    >
                                        🗑️ Eliminar
                                    </button>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                {/* FORMULARIO DE ALTA / EDICIÓN */}
                <div style={{ flex: '1 1 350px', background: editingId ? '#EFF6FF' : COLORS.cream, padding: 25, borderRadius: 12, height: 'fit-content', border: `1px solid ${editingId ? COLORS.blue : COLORS.pink}`, transition: '0.3s' }}>
                    
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15 }}>
                        <h3 style={{ margin: 0, color: editingId ? COLORS.blue : COLORS.dark }}>
                            {editingId ? '✏️ Editando Usuario' : '➕ Nuevo Usuario'}
                        </h3>
                        {editingId && (
                            <button onClick={handleCancelEdit} style={{ background: 'transparent', border: 'none', color: '#666', cursor: 'pointer', textDecoration: 'underline', fontSize: 12 }}>
                                Cancelar
                            </button>
                        )}
                    </div>

                    <p style={{ fontSize: 13, color: '#666', marginBottom: 20 }}>
                        {editingId ? 'Modifica los datos necesarios. Deja la contraseña en blanco para no cambiarla.' : 'Crea un acceso para tu personal.'}
                    </p>

                    <form onSubmit={handleSubmit}>
                        
                        {/* INPUT 1: USUARIO */}
                        <label style={labelStyle}>Nombre de Usuario (Login)</label>
                        <input 
                            type="text" 
                            required 
                            placeholder="ej: sucursal_centro"
                            style={inputStyle} 
                            value={form.username} 
                            onChange={e => setForm({...form, username: e.target.value.replace(/\s+/g, '').toLowerCase()})} 
                        />

                        {/* INPUT 2: CONTRASEÑA */}
                        <label style={labelStyle}>
                            {editingId ? 'Nueva Contraseña (Opcional)' : 'Contraseña'}
                        </label>
                        <input 
                            type="text" 
                            required={!editingId} // Obligatoria solo al crear
                            placeholder={editingId ? "Dejar vacío para mantener actual" : "Mínimo 6 caracteres"}
                            style={inputStyle} 
                            value={form.password} 
                            onChange={e => setForm({...form, password: e.target.value})} 
                        />

                        {/* INPUT 3: SUCURSAL */}
                        <label style={labelStyle}>Asignar a Sucursal</label>
                        <select 
                            required 
                            style={inputStyle} 
                            value={form.sucursalId} 
                            onChange={e => setForm({...form, sucursalId: e.target.value})}
                        >
                            <option value="">-- Selecciona --</option>
                            {sucursales.map(s => (
                                <option key={s.id} value={s.id}>{s.nombre}</option>
                            ))}
                        </select>

                        <button 
                            type="submit" 
                            disabled={saving}
                            style={{ 
                                width: '100%', padding: 12, 
                                background: editingId ? COLORS.blue : COLORS.green, 
                                color: 'white', border: 'none', borderRadius: 8, 
                                fontWeight: 'bold', cursor: 'pointer', marginTop: 10 
                            }}
                        >
                            {saving ? 'Guardando...' : (editingId ? 'Guardar Cambios' : 'Crear Usuario')}
                        </button>
                    </form>
                </div>

            </div>
        </ClientLayout>
    );
}

export default MisEmpleados;